import React, { useState } from 'react';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { StatusPill } from '../components/StatusPill';
import { IconWrap } from '../components/IconWrap';
import {
  CiViewList,
  CiShare1,
  CiUser,
  CiCalendar,
  CiRead,
  CiBookmark,
  CiLock,
  CiDroplet,
} from 'react-icons/ci';
import { PiFactoryThin } from 'react-icons/pi';
import { sectors } from './Home/data/sectors';
import useTheme from '../hooks/useTheme';

export const Membership: React.FC = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    country: '',
    email: '',
    phone: '',
    sector: '',
    interests: [] as string[],
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const benefits = [
    {
      title: 'Tenders Alert & Database',
      status: 'COMING SOON' as const,
      phase: 'Phase 3' as const,
      icon: <CiViewList />,
      description: 'Access to a comprehensive database of tenders and procurement opportunities in Korea and Egypt.',
      bullets: [
        'Real-time tender alerts',
        'Filtered by sector and country',
        'Detailed tender information',
      ],
    },
    {
      title: 'Business Opportunities Exchange',
      status: 'COMING SOON' as const,
      phase: 'Phase 3' as const,
      icon: <CiShare1 />,
      description: 'Submit and browse business opportunities, partner searches, and collaboration requests.',
      bullets: [
        'Submit opportunities',
        'Browse available matches',
        'Direct partner connections',
      ],
    },
    {
      title: 'Member Directory',
      status: 'COMING SOON' as const,
      phase: 'Phase 2' as const,
      icon: <CiUser />,
      description: 'Connect with other BCBH members through a searchable directory of companies.',
      bullets: [
        'Searchable member profiles',
        'Company details and sectors',
        'Direct contact information',
      ],
    },
    {
      title: 'Events & Missions Access',
      status: 'LIVE' as const,
      phase: 'Phase 1' as const,
      icon: <CiCalendar />,
      description: 'Priority access to BCBH events, business missions, and networking opportunities.',
      bullets: [
        'Event RSVP management',
        'Mission application priority',
        'Exclusive member events',
      ],
    },
    {
      title: 'Publications & Briefings Library',
      status: 'COMING SOON' as const,
      phase: 'Phase 2' as const,
      icon: <CiRead />,
      description: 'Access to exclusive market reports, sector briefings, and industry insights.',
      bullets: [
        'Market analysis reports',
        'Sector-specific briefings',
        'Country market guides',
      ],
    },
    {
      title: 'Training & Recruitment Programs',
      status: 'COMING SOON' as const,
      phase: 'Phase 2' as const,
      icon: <CiBookmark />,
      description: 'Participate in training programs and access recruitment opportunities.',
      bullets: [
        'Skills development workshops',
        'Technology transfer programs',
        'Recruitment matching',
      ],
    },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    console.log('Waitlist submission:', formData);
    setShowSuccess(true);
    setFormData({
      fullName: '',
      company: '',
      country: '',
      email: '',
      phone: '',
      sector: '',
      interests: [],
      message: '',
    });
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const opportunities = [
    {
      title: 'Korean wastewater solutions partner search (Egypt)',
      sector: 'Water & Wastewater',
      country: 'Egypt',
      stage: 'Draft',
      icon: <CiDroplet />,
    },
    {
      title: 'Egyptian industrial supplier matchmaking (Korea)',
      sector: 'Industrial Products',
      country: 'Korea',
      stage: 'Screening',
      icon: <PiFactoryThin />,
    },
  ];

  return (
    <>
      <PageHero
        title="Membership & Waitlist"
        subtitle="Join BCBH and access exclusive member benefits when Phase 2 launches."
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Membership' }]}
      />

      <Section>
        <div
          style={{
            textAlign: 'center',
            marginBottom: '48px',
          }}
        >
          <Badge status="COMING SOON" />
          <h2
            style={{
              fontSize: '34px',
              fontWeight: 700,
              color: theme.text.primary,
              marginTop: '20px',
              marginBottom: '16px',
            }}
            className="sectionTitle"
          >
            Become a Member (Phase 2)
          </h2>
          <p
            style={{
              fontSize: '18px',
              color: theme.text.muted,
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}
          >
            BCBH membership will provide access to exclusive features, resources, and networking opportunities. Join the waitlist to be notified when membership opens.
          </p>
        </div>

        {/* Business Opportunities Exchange Preview */}
        <Card style={{ marginBottom: '48px', border: `2px solid ${theme.colors.line}` }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IconWrap icon={<CiShare1 />} size={24} color="primary" />
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                }}
              >
                Business Opportunities Exchange
              </h3>
            </div>
            <StatusPill status="COMING SOON" />
          </div>
          <p
            style={{
              color: theme.text.muted,
              marginBottom: '20px',
              lineHeight: 1.6,
            }}
          >
            Preview of the Business Opportunities Exchange module (Phase 3). Members will be able to submit and browse opportunities.
          </p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {opportunities.map((opp, idx) => (
              <div
                key={idx}
                style={{
                  padding: '16px',
                  backgroundColor: theme.background.panel,
                  borderRadius: theme.borderRadius.r1,
                  border: `1px solid ${theme.colors.line}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px',
                  }}
                >
                  <IconWrap icon={opp.icon} size={20} color="primary" />
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: theme.text.primary,
                    }}
                  >
                    {opp.title}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginBottom: '12px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: theme.text.muted,
                      padding: '3px 8px',
                      backgroundColor: theme.background.card,
                      borderRadius: '4px',
                    }}
                  >
                    {opp.sector}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      color: theme.text.muted,
                      padding: '3px 8px',
                      backgroundColor: theme.background.card,
                      borderRadius: '4px',
                    }}
                  >
                    {opp.country}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      color: theme.text.muted,
                      padding: '3px 8px',
                      backgroundColor: theme.background.card,
                      borderRadius: '4px',
                    }}
                  >
                    {opp.stage}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button variant="ghost" disabled style={{ fontSize: '12px', padding: '6px 12px', flex: 1 }}>
                    <IconWrap icon={<CiLock />} size={12} color="muted" />
                    <span style={{ marginLeft: '6px' }}>Submit</span>
                  </Button>
                  <Button variant="ghost" disabled style={{ fontSize: '12px', padding: '6px 12px', flex: 1 }}>
                    <IconWrap icon={<CiLock />} size={12} color="muted" />
                    <span style={{ marginLeft: '6px' }}>Apply</span>
                  </Button>
                </div>
                <p
                  style={{
                    fontSize: '10px',
                    color: theme.text.muted,
                    marginTop: '8px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                  }}
                >
                  Module opens in Phase 3
                </p>
              </div>
            ))}
          </div>
        </Card>

        <h3
          style={{
            fontSize: '28px',
            fontWeight: 700,
            color: theme.text.primary,
            marginBottom: '32px',
            textAlign: 'center',
          }}
        >
          Member Benefits
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
            marginBottom: '64px',
          }}
          className="benefitsGrid"
        >
          {benefits.map((benefit, idx) => (
            <Card key={idx} hoverable>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                }}
              >
                <IconWrap icon={benefit.icon} size={28} color="primary" />
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: theme.text.primary,
                      marginBottom: '4px',
                    }}
                  >
                    {benefit.title}
                  </h4>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <Badge status={benefit.status} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: theme.text.muted,
                    padding: '3px 8px',
                    backgroundColor: theme.background.panel,
                    borderRadius: '6px',
                    border: `1px solid ${theme.colors.line}`,
                  }}
                >
                  {benefit.phase}
                </span>
              </div>
              <p
                style={{
                  color: theme.text.muted,
                  lineHeight: 1.6,
                  marginBottom: '16px',
                  fontSize: '14px',
                }}
              >
                {benefit.description}
              </p>
              <div
                style={{
                  fontSize: '13px',
                  color: theme.text.muted,
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: '8px', color: theme.text.primary }}>
                  What you get:
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  {benefit.bullets.map((bullet, bulletIdx) => (
                    <li
                      key={bulletIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                      }}
                    >
                      <span style={{ color: theme.colors.green.g1, flexShrink: 0 }}>•</span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
        </div>

        {/* Phasing Strip */}
        <Card
          style={{
            maxWidth: '700px',
            margin: '0 auto 48px',
            backgroundColor: theme.background.panel,
            border: `1px solid ${theme.colors.line}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            {[
              { phase: 'Phase 1', status: 'LIVE' as const, label: 'Launch site' },
              { phase: 'Phase 2', status: 'COMING SOON' as const, label: 'Member area' },
              { phase: 'Phase 3', status: 'COMING SOON' as const, label: 'Business portal' },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  textAlign: 'center',
                  flex: 1,
                  minWidth: '150px',
                }}
              >
                <StatusPill status={item.status} size="small" />
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.text.primary,
                    marginTop: '8px',
                    marginBottom: '4px',
                  }}
                >
                  {item.phase}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: theme.text.muted,
                  }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h3
            style={{
              fontSize: '28px',
              fontWeight: 700,
              color: theme.text.primary,
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            Join the Waitlist
          </h3>

          {showSuccess && (
            <div
              style={{
                padding: '16px',
                backgroundColor: theme.colors.green.g1,
                color: theme.text.primary,
                borderRadius: theme.borderRadius.r1,
                marginBottom: '24px',
                textAlign: 'center',
              }}
            >
              Thanks — we'll contact you when membership opens.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.text.primary,
                    marginBottom: '8px',
                  }}
                >
                  Full Name <span style={{ color: theme.colors.green.g1 }}>*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: theme.background.panel,
                    border: `1px solid ${errors.fullName ? 'red' : theme.colors.line}`,
                    borderRadius: theme.borderRadius.r1,
                    color: theme.text.primary,
                    fontSize: '16px',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ fontSize: '12px', color: theme.text.muted, marginTop: '4px' }}>
                  Your full legal name as it appears on official documents
                </div>
                {errors.fullName && (
                  <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                    {errors.fullName}
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.text.primary,
                    marginBottom: '8px',
                  }}
                >
                  Company <span style={{ color: theme.colors.green.g1 }}>*</span>
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: theme.background.panel,
                    border: `1px solid ${errors.company ? 'red' : theme.colors.line}`,
                    borderRadius: theme.borderRadius.r1,
                    color: theme.text.primary,
                    fontSize: '16px',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ fontSize: '12px', color: theme.text.muted, marginTop: '4px' }}>
                  Your company or organization name
                </div>
                {errors.company && (
                  <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                    {errors.company}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                }}
                className="formRow"
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: theme.text.primary,
                      marginBottom: '8px',
                    }}
                  >
                    Country <span style={{ color: 'var(--g1)' }}>*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: theme.background.panel,
                      border: `1px solid ${errors.country ? 'red' : theme.colors.line}`,
                      borderRadius: theme.borderRadius.r1,
                      color: theme.text.primary,
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="">Select country</option>
                    <option value="Egypt">Egypt</option>
                    <option value="Korea">Korea</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.country && (
                    <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                      {errors.country}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: theme.text.primary,
                      marginBottom: '8px',
                    }}
                  >
                    Sector
                  </label>
                  <select
                    name="sector"
                    value={formData.sector}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: theme.background.panel,
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r1)',
                      color: theme.text.primary,
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="">Select sector</option>
                    {sectors.map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                }}
                className="formRow"
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: theme.text.primary,
                      marginBottom: '8px',
                    }}
                  >
                    Email <span style={{ color: theme.colors.green.g1 }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: theme.background.panel,
                      border: `1px solid ${errors.email ? 'red' : theme.colors.line}`,
                      borderRadius: theme.borderRadius.r1,
                      color: theme.text.primary,
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  />
                  {errors.email && (
                    <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                      {errors.email}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: theme.text.primary,
                      marginBottom: '8px',
                    }}
                  >
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: theme.background.panel,
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r1)',
                      color: theme.text.primary,
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.text.primary,
                    marginBottom: '12px',
                  }}
                >
                  Interests
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {[
                    'Events & Missions',
                    'Tenders & Alerts',
                    'Opportunities Exchange',
                    'Training & Recruitment',
                    'Publications & Reports',
                  ].map((interest) => (
                    <label
                      key={interest}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: theme.text.primary,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.interests.includes(interest)}
                        onChange={() => handleCheckboxChange(interest)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                        }}
                      />
                      {interest}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.text.primary,
                    marginBottom: '8px',
                  }}
                >
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: theme.background.panel,
                    border: `1px solid ${theme.colors.line}`,
                    borderRadius: theme.borderRadius.r1,
                    color: theme.text.primary,
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </div>

              <Button type="submit" variant="primary" style={{ width: '100%' }}>
                Submit Waitlist Application
              </Button>
            </div>
          </form>
        </Card>
      </Section>
    </>
  );
};

